import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ChatResizer from './ChatResizer';
import { setChatOpen } from '../../state/uiSlice';
import { useCopilot } from '../../api/copilot';
import {
  AgentStatusDot, CopilotComposer, CopilotThread, SpeakingIndicator,
} from '../copilot';
import {
  callSafely, isSheetLayout, offersReconnect, restingText,
} from '../copilot/util';

/**
 * Short prompts about whatever is on screen. The agent receives the screen,
 * its tab and its subject with every question, so "this" needs no naming.
 */
const QUICK = ['What am I looking at?', 'Why is it behind?', 'Run attribution'];

/** The newest confirmation card still waiting for Run or Cancel, or null. */
function openConfirmId(messages) {
  const list = Array.isArray(messages) ? messages : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const m = list[i];
    if (m?.kind === 'confirm' && m.card?.status === 'confirm') return m.id;
  }
  return null;
}

const READY_TEXT =
  'Ask about any client, fund, factor, risk or tax question — answers cite the holdings and metrics ' +
  'behind them. Type, or press the mic to talk.';

/**
 * The copilot as a docked side panel, opened from the top bar.
 *
 * A column in the app's own grid rather than something floating over it, so
 * the page is narrowed by it instead of hidden under it — a reader can hold a
 * table and the conversation about it on screen at once, which is the whole
 * point of asking about what you are looking at.
 *
 * It stays mounted when closed, collapsed to a zero-width sliver by the
 * stylesheet: an element that is not rendered cannot animate away, so
 * unmounting it would make opening slide and closing snap. Being mounted also
 * keeps the transcript and its scroll position across a close and reopen.
 *
 * There is no resting bar. The top bar's button is the only way in and out. A
 * press outside does NOT close it, unlike the drawer — a docked panel is
 * somewhere you leave open while you work, and every click on the table beside
 * it would otherwise dismiss it.
 *
 * The conversation itself — lines, cards, live captions, the composer — is the
 * shared `components/copilot` set, so the dock and the drawer cannot drift
 * apart in what they show.
 *
 * On a seat's landing screen the assistant is in the page (`AgentHome`), so
 * the shell passes `hidden` and the column stays collapsed there; the open
 * state is kept, and the column is back on the next screen.
 *
 * A new confirmation card opens it when neither copilot surface is open. The
 * agent asks for Run by voice with the panel shut, and a card nobody can see
 * would only expire. It opens once per card, so a reader who closes it again is
 * left alone, and not on the phone layout, where the panel is a full-screen
 * sheet that would cover the form being confirmed — the top bar's button says
 * a card is waiting instead.
 */
export default function ChatDock({ hidden = false }) {
  const dispatch = useDispatch();
  const { chatOpen, drawerOpen } = useSelector((s) => s.ui);
  /* `hidden` is the shell saying the assistant is in the page itself (a seat's
     landing screen): the column stays collapsed there whatever `chatOpen`
     says, and reopens by itself on the next screen. */
  const open = chatOpen && !hidden;
  const {
    messages, connection = 'idle', reconnect, stalled = false,
  } = useCopilot();
  const empty = !messages || messages.length === 0;

  /* Leaving a landing screen with a conversation under way: the assistant was
     in the page there, so the dock opens on the next screen with the same
     thread — otherwise an answer that moved the reader looks like it went
     quiet. Not on the phone layout, where the dock is a full-screen sheet. */
  const wasHidden = useRef(hidden);
  useEffect(() => {
    const left = wasHidden.current && !hidden;
    wasHidden.current = hidden;
    if (!left || empty || chatOpen || drawerOpen || isSheetLayout()) return;
    dispatch(setChatOpen(true));
  }, [hidden, empty, chatOpen, drawerOpen, dispatch]);

  const confirmId = openConfirmId(messages);
  const seenConfirm = useRef(confirmId);
  useEffect(() => {
    if (!confirmId || confirmId === seenConfirm.current) return;
    seenConfirm.current = confirmId;
    if (hidden || chatOpen || drawerOpen || isSheetLayout()) return;
    dispatch(setChatOpen(true));
  }, [confirmId, chatOpen, drawerOpen, hidden, dispatch]);

  return (
    /* `inert` while collapsed, so a keyboard cannot tab into a panel nobody
       can see. The stylesheet hides it too, but only after the close finishes
       — this covers the interval in between. */
    <aside className="chatpane" aria-label="KeepInvesting assistant" aria-hidden={!open} inert={!open ? '' : undefined}>
      <ChatResizer />
      <header className="chatpane-head">
        <b>KeepInvesting</b>
        {/* The connection, stated rather than implied: a panel that looks ready
            and answers nothing is the state worth naming. */}
        <AgentStatusDot />
        <span className="cp-head-tools">
          <SpeakingIndicator />
          {offersReconnect(connection, stalled) && (
            <button type="button" className="cp-link-btn" onClick={() => callSafely(reconnect)}>
              Reconnect
            </button>
          )}
        </span>
        <button
          type="button"
          className="chatpane-x"
          title="Close assistant"
          aria-label="Close assistant"
          onClick={() => dispatch(setChatOpen(false))}
        >
          ✕
        </button>
      </header>

      <CopilotThread variant="dock" greeting={empty ? restingText(connection, READY_TEXT) : null} />

      <div className="chatpane-foot">
        <CopilotComposer placeholder="Ask anything…" chips={QUICK} />
      </div>
    </aside>
  );
}
